import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Modal from '../../ui/atoms/Modal'
import { Text, View, StyleSheet } from 'react-native'
import { SButton, Spacer } from '../../ui/atoms'
import { IconWarningInfo } from '../../assets/icons'
import { useStore } from '../../store'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { observer } from 'mobx-react'
import { useMemo } from 'react'
import { useRef } from 'react'
import { useResponsive } from '../hooks'
import { BtAlert } from '@linqpal/components/src/ui'

interface propTypes {
  onActive?: () => void
  onIdle?: () => void
  onLogout?: () => void
}

const timeOut = 1000 * 60 * 28 // 28 min
const logoutTime = 1000 * 60 * 2 // 2 min

const IdleTimeOutHandler = observer(
  ({ onActive, onIdle, onLogout }: propTypes) => {
    const [showModal, setShowModal] = useState(false)
    const [isLogout, setLogout] = useState(false)
    const [timeOutInfo, showTimeOutInfo] = useState(false)
    const store = useStore()
    const logoutTick = useRef<any>(0)
    const timer = useRef<any>(0)
    const events = useMemo(
      () => ['click', 'load', 'keydown', 'pointerdown'],
      [],
    )

    const logout = useCallback(() => {
      store.logOut()
    }, [store])

    const clearLogoutTimer = useCallback(() => {
      if (logoutTick.current) {
        clearTimeout(logoutTick.current)
        logoutTick.current = null
      }
    }, [])

    const startTimer = useCallback(() => {
      clearLogoutTimer()
      if (timer.current) {
        clearTimeout(timer.current)
      }

      timer.current = setTimeout(() => {
        const lastInteractionTime = localStorage.getItem('lastInteractionTime')
        const diff = moment().diff(moment(lastInteractionTime))
        if (isLogout) {
          clearTimeout(timer.current)
          clearLogoutTimer()
        } else {
          if (diff < timeOut) {
            startTimer()
            onActive && onActive()
          } else {
            onIdle && onIdle()
            setShowModal(true)
          }
        }
      }, timeOut)
    }, [clearLogoutTimer, isLogout, onActive, onIdle])

    const eventHandler = useCallback(() => {
      if (!isLogout) {
        localStorage.setItem('lastInteractionTime', moment().toString())
        if (timer.current) {
          onActive && onActive()
          startTimer()
        }
      }
    }, [isLogout, onActive, startTimer])

    const addEvents = useCallback(() => {
      events.forEach((eventName) => {
        window.addEventListener(eventName, eventHandler)
      })
      startTimer()
    }, [events, eventHandler, startTimer])

    const removeEvents = useCallback(() => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, eventHandler)
      })
    }, [events, eventHandler])

    const handleContinueSession = () => {
      setShowModal(false)
      setLogout(false)
      startTimer()
    }
    const handleLogout = () => {
      removeEvents()
      clearLogoutTimer()
      setLogout(true)
      onLogout && onLogout()
      setShowModal(false)
      logout()
    }

    useEffect(() => {
      addEvents()
      return () => {
        removeEvents()
        clearTimeout(timer.current)
        clearLogoutTimer()
      }
    }, [addEvents, clearLogoutTimer, removeEvents])

    useEffect(() => {
      if (showModal && store.userStore.isAuthenticated) {
        logoutTick.current = setTimeout(() => {
          showTimeOutInfo(true)
          setShowModal(false)
          clearTimeout(timer.current)
          logout()
        }, logoutTime)
      }
    }, [showModal, store.userStore.isAuthenticated, logout])

    return (
      <>
        <IdleTimeOutWarning
          visible={showModal && store.userStore.isAuthenticated}
          handleContinue={handleContinueSession}
          handleLogout={handleLogout}
        />
        <IdleTimeOutInfo
          visible={timeOutInfo}
          onClose={() => {
            showTimeOutInfo(false)
          }}
        />
      </>
    )
  },
)

export default IdleTimeOutHandler

const IdleTimeOutWarning = ({ visible, handleContinue, handleLogout }) => {
  const { t } = useTranslation('global')
  const { sm } = useResponsive()
  return visible ? (
    <BtAlert
      visible={visible}
      type="warning"
      width={sm ? 600 : 335}
      onClose={handleContinue}
      verticalButtons={!sm}
      buttons={[
        {
          label: t('logOut'),
          appearance: 'outline',
          style: styles.btnDesktop,
          onPress: handleLogout,
        },
        {
          label: t('TimeoutHandler.continue-session'),
          style: styles.btnDesktop,
          onPress: handleContinue,
        },
      ]}
    >
      {sm && <Spacer height={20} />}
      <Text style={sm ? styles.titleDesktop : styles.titleMobile}>
        {t('TimeoutHandler.session-about-to-expire')}
      </Text>
      <Text style={sm ? styles.subtitleDesktop : styles.subtitleMobile}>
        {t('TimeoutHandler.you-will-be-logged')}
      </Text>
    </BtAlert>
  ) : null
}

export const IdleTimeOutInfo = ({ visible, onClose }) => {
  const { t } = useTranslation('global')
  return visible ? (
    <Modal
      visible={visible}
      onClose={onClose}
      style={{ alignItems: 'center' }}
      backDropAction={false}
      animationIn="fadeIn"
      animationOut="fadeOut"
    >
      <View style={styles.timeoutInfoWrapper}>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <IconWarningInfo stroke={'#EB9928'} width={40} height={50} />
        <Text style={styles.sessionExpiredLabel}>
          {t('TimeoutHandler.session-expired')}
        </Text>
        <SButton onPress={onClose}>{t('TimeoutHandler.login')}</SButton>
      </View>
    </Modal>
  ) : null
}

const styles = StyleSheet.create({
  titleDesktop: {
    color: '#003353',
    fontSize: 28,
    fontWeight: '600',
    lineHeight: 28,
    marginBottom: 12,
    textAlign: 'center',
  },
  titleMobile: {
    color: '#003353',
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 28,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitleDesktop: {
    color: '#003353',
    fontWeight: '400',
    fontSize: 18,
    lineHeight: 24,
    marginBottom: 46,
    textAlign: 'center',
  },
  subtitleMobile: {
    color: '#668598',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 26,
    textAlign: 'center',
  },
  btnDesktop: {
    minWidth: 224,
  },
  timeoutInfoWrapper: {
    height: 235,
    backgroundColor: 'white',
    width: 420,
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'center',
    padding: 30,
    justifyContent: 'space-between',
  },
  sessionExpiredLabel: { fontSize: 16, lineHeight: 24 },
})
